import { useQuery } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import {
  BUCKET_ORDER,
  type AgingCustomerOpen,
  type BillsAgingResponse,
} from '@/types/backend'

export type AgingCustomerSlice = {
  customerId: string
  name: string
  contact?: string
  amount: number
}

/** ----- Bills summary (/_summary?year=YYYY) ----- */
export const useBillsSummary = (year?: number, enabled = true) =>
  useQuery({
    queryKey: ['bills', '_summary', { year }],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get('/bills/_summary', {
        params: year ? { year } : undefined,
      })
      // Service renvoie:
      // { orders:number, totalSales:number, monthly:[{month,total}], salesByCustomer:[{customerId,name,total,orders}], quantityByCategory:[{category, qty}] }
      return data as {
        orders: number
        totalSales: number
        monthly: { month: number; total: number }[]
        salesByCustomer: {
          customerId: string
          name: string
          total: number
          orders: number
        }[]
        quantityByCategory: { category: string | null; qty: number }[]
      }
    },
  })

/** ----- Receivables fin de mois (/_receivables-eom?year=YYYY) ----- */
export const useReceivablesEOM = (year?: number, enabled = true) =>
  useQuery({
    queryKey: ['bills', '_receivables-eom', { year }],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get('/bills/_receivables-eom', {
        params: year ? { year } : undefined,
      })
      // Service renvoie: { monthly:[{ ym:'YYYY-MM', total:number }] }
      return data as { monthly: { ym: string; total: number }[] }
    },
  })

/** ----- Receivables “monthly” (/_receivables-monthly?year=YYYY) ----- */
export const useReceivablesMonthly = (year?: number, enabled = true) =>
  useQuery({
    queryKey: ['bills', '_receivables-monthly', { year }],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get('/bills/_receivables-monthly', {
        params: year ? { year } : undefined,
      })
      // Service renvoie: { monthly:[{ ym:'YYYY-MM', total:number }] }
      return data as { monthly: { ym: string; total: number }[] }
    },
  })

/** ----- Aging (/_aging?asOf=YYYY-MM-DD&terms=30) ----- */

export type AgingLabel = 'Current' | '1-30' | '31-60' | '61-90' | '90+'

export const AGING_COLORS: Record<AgingLabel, string> = {
  Current: '#22c55e',
  '1-30': '#84cc16',
  '31-60': '#f59e0b',
  '61-90': '#fb7185',
  '90+': '#ef4444',
}

// Transforme la réponse en data pour <ResponsiveBar />
function toAgingBarViz(res: BillsAgingResponse) {
  const byLabel = new Map(res.buckets.map((b) => [b.label, b]))
  const keys = BUCKET_ORDER.filter((k) => byLabel.has(k))
  const row: Record<string, number | string> = { tranche: 'Échéancier' }
  keys.forEach((k) => (row[k] = byLabel.get(k)?.total ?? 0))
  return {
    data: [row],
    keys,
    colors: keys.map((k) => AGING_COLORS[k as AgingLabel]),
  }
}

export async function getBillsAging(params?: {
  asOf?: string
  terms?: number
}) {
  const sp = new URLSearchParams()
  if (params?.asOf) sp.set('asOf', params.asOf)
  if (params?.terms != null) sp.set('terms', String(params.terms))
  const qs = sp.toString() ? `?${sp.toString()}` : ''
  return apiClient
    .get<BillsAgingResponse>(`/bills/_aging${qs}`)
    .then((r) => r.data)
}

export function useBillsAging(asOf?: string, terms?: number, enabled = true) {
  // si le backend a des valeurs par défaut, on ne force PAS asOf/terms pour l’activation
  const hasValidTerms = terms == null || Number.isFinite(terms)
  const isEnabled = Boolean(enabled && hasValidTerms)

  return useQuery({
    queryKey: ['analytics', 'aging', asOf ?? 'default', terms ?? 'default'],
    queryFn: () => getBillsAging({ asOf, terms }),
    enabled: isEnabled,
    // expose des champs pratiques pour le front
    select: (
      res
    ): BillsAgingResponse & {
      totals: { brut: number; credits: number; net: number }
      viz: {
        data: Array<Record<string, number | string>>
        keys: string[]
        colors: string[]
      }
      customersOpen: AgingCustomerOpen[]
    } => ({
      ...res,
      totals: {
        brut: res.grandTotalBrut,
        credits: res.creditsUnapplied,
        net: res.grandTotalNet,
      },
      viz: toAgingBarViz(res),
      // liste prête à afficher (filtrée et triée)
      customersOpen: [...(res.customers ?? [])]
        .filter((c) => (c.open ?? 0) > 0)
        .sort((a, b) => b.open - a.open),
    }),
  })
}

/** ----- Payments summary (/payments/_summary?from&to) ----- */
export const usePaymentsSummary = (
  from?: string,
  to?: string,
  enabled = true
) =>
  useQuery({
    queryKey: ['payments', '_summary', { from, to }],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get('/payments/_summary', {
        params: { from, to },
      })
      // Service renvoie:
      // { totals:{ in,out,net }, byMethod:[{method,total}], daily:[{day,total}], monthly:[{ym,total}], byCustomer:[{customerId,name,total}] }
      return data as {
        totals: { in: number; out: number; net: number }
        byMethod: { method: string; total: number }[]
        daily: { day: string; total: number }[]
        monthly: { ym: string; total: number }[]
        byCustomer: { customerId: string; name: string; total: number }[]
      }
    },
  })
